import React from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const Disclaimers = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-page-container">
      <SEO
        title="Disclaimers & Fan Art Notice"
        description="Star.Ryhwa official fanmade art disclaimers, trademark notices, and product disclosures."
        canonicalUrl="/disclaimers"
      />
      <div className="legal-page-inner">
        {/* Breadcrumb Bar */}
        <div className="legal-breadcrumbs">
          <Link to="/">Home</Link>
          <span className="legal-breadcrumb-separator">/</span>
          <span>Trust &amp; Legal Center</span>
          <span className="legal-breadcrumb-separator">/</span>
          <span className="legal-breadcrumb-current">Disclaimers &amp; Fan Art Notice</span>
        </div>

        {/* Document Header */}
        <header className="legal-doc-header">
          <h1 className="legal-doc-title">Disclaimers &amp; Fan Art Notice</h1>
          <p className="legal-doc-description">
            Legal non-affiliation disclosures, First Sale Doctrine protection for handcrafted album collages, product safety guidelines, and statutory DMCA copyright notice protocols.
          </p>

          <div className="legal-meta-bar">
            <div className="legal-meta-items">
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Last Updated: <strong>September 2026</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Estimated Read: <strong>5 min read</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Defense: <strong>First Sale Doctrine (17 U.S.C. § 109)</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Copyright Regime: <strong>DMCA (17 U.S.C. § 512)</strong>
              </span>
            </div>
            <button type="button" onClick={handlePrint} className="legal-print-btn" title="Print or Save as PDF">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / Save PDF
            </button>
          </div>
        </header>

        {/* Navigation Tab Bar */}
        <LegalNav />

        {/* Executive Summary */}
        <div className="legal-doc-summary">
          <h2>Summary of Disclaimers</h2>
          <ul>
            <li><strong>Independent Fan Studio:</strong> Star.Ryhwa is an independent fan studio not affiliated with, endorsed by, or sponsored by HYBE, JYP, SM, YG, KQ, WakeOne, or any entertainment label.</li>
            <li><strong>Lawful First Sale Collages:</strong> Collages are individually handcrafted from physical elements of officially purchased retail albums. We never mass-print or digitally duplicate copyrighted imagery.</li>
            <li><strong>Choking Hazard Warning:</strong> Custom charms and keychain accessories contain small parts and are not suitable for children under the age of 3. Refillable bottles ship empty without chemical liquids.</li>
            <li><strong>Statutory DMCA Portal:</strong> Rights holders may submit verified infringement notices containing the 6 statutory elements specified by 17 U.S.C. § 512(c)(3) to our designated copyright desk.</li>
          </ul>
        </div>

        {/* Main Two-Column Layout */}
        <div className="legal-grid-layout">
          {/* Main Content Column */}
          <article className="legal-main-content">
            {/* Section 1 */}
            <section id="section-1" className="legal-doc-section">
              <h2>1. Independent Fan Studio — Non-Affiliation Statement</h2>
              <p>
                Star.Ryhwa is an <strong>independent, fan-operated artisanal studio</strong> based in Hawaii. All artist names, group names, logos, character designs, song titles, and official media referenced across this store remain the exclusive intellectual property and registered trademarks of their respective entertainment labels, management agencies, and copyright holders.
              </p>
              <p>
                Our fan creations are <strong>not official licensed merchandise</strong>. Star.Ryhwa is not affiliated with, endorsed by, sponsored by, or associated with any of the following entities:
              </p>
              <ul>
                <li><strong>HYBE / Big Hit Music / PLEDIS / BELIFT LAB:</strong> BTS, TOMORROW X TOGETHER, ENHYPEN, SEVENTEEN, &amp;TEAM</li>
                <li><strong>JYP Entertainment:</strong> Stray Kids, TWICE, ITZY, NMIXX, NiziU</li>
                <li><strong>SM Entertainment:</strong> NCT (127, DREAM, WayV), EXO, aespa, RIIZE, SHINee</li>
                <li><strong>YG Entertainment:</strong> BLACKPINK, TREASURE, BABYMONSTER</li>
                <li><strong>KQ Entertainment:</strong> ATEEZ, xikers</li>
                <li><strong>WakeOne / Stone Music Entertainment:</strong> ZEROBASEONE (ZB1), Kep1er</li>
                <li><strong>Gaming &amp; Anime Franchises:</strong> Nintendo Co., Ltd. (Pokémon), Toei Animation Co., Ltd. (One Piece, Sailor Moon), LINE Friends Corporation (BT21)</li>
              </ul>
              <p>
                All trademarks, service marks, and trade names used on this site are for descriptive and identification purposes only under nominative fair use principles.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="legal-doc-section">
              <h2>2. K-Pop Album Collages — Handcrafted Artwork &amp; First Sale Doctrine</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>First Sale Doctrine (17 U.S.C. § 109):</strong> Each Star.Ryhwa collage is a unique, one-of-a-kind physical art piece handcrafted using genuine, physical album inclusions (such as photobook pages, postcards, lyric sheets, and sticker sheets) taken from officially licensed albums lawfully purchased at retail from authorized distributors.
                </p>
              </div>
              <p>
                In accordance with the United States First Sale Doctrine (17 U.S.C. § 109), the lawful owner of an authorized physical copy of a copyrighted work is entitled to resell, modify, or physically display that particular copy without infringing the copyright owner's distribution rights.
              </p>
              <p>Our collage art is strictly governed by the following commitments:</p>
              <ul>
                <li><strong>Genuine Physical Materials Only:</strong> We never mass-print, digitally scan, photostat, or counterfeit official imagery. Every collage element is cut by hand from an authentic retail-purchased album.</li>
                <li><strong>Single-Copy Original Works:</strong> Every piece is assembled individually by hand; no two collages are identical.</li>
                <li><strong>No Market Confusion:</strong> Our collages are clearly labeled and marketed as transformative, handmade fan creations and are not intended to compete with or substitute for original album sales.</li>
                <li><strong>Support for Artists:</strong> By purchasing authentic albums to create these pieces, our studio directly contributes to official album sales charts and supports the creators.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="legal-doc-section">
              <h2>3. Decorative Keychains &amp; Product Safety Notice</h2>
              <p>
                Star.Ryhwa's custom hand sanitizer keychains and charm accessories are sold as <strong>decorative lifestyle accessories</strong>. Please observe all applicable safety precautions:
              </p>
              <div className="legal-doc-note">
                <p>
                  <strong>Choking Hazard &amp; Small Parts Warning:</strong> Handcrafted keychains, charms, bead strings, and spray caps contain small parts. They are not toys and are <strong>not suitable for unsupervised children under the age of 3</strong> due to choking hazards. Keep away from small children and pets.
                </p>
              </div>
              <ul>
                <li><strong>Bottles Shipped Empty:</strong> Spray keychains are shipped empty. Sanitizer fluid is not included.</li>
                <li><strong>Non-Medical Accessories:</strong> The spray containers and decorative housings are not medical devices and have not been evaluated by the FDA.</li>
                <li><strong>Flammability &amp; Responsible Use:</strong> Alcohol-based hand sanitizer is flammable. Do not use, store, or refill near open flames, lit cigarettes, or high-heat environments.</li>
                <li><strong>External Application:</strong> Any liquid sanitizer used with these containers is for external hand application only. Avoid direct contact with eyes, broken skin, or mucous membranes.</li>
              </ul>
              <p>
                Star.Ryhwa disclaims all liability for chemical misuse, leakage, staining, or bodily injury resulting from third-party liquids loaded into our accessories.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="legal-doc-section">
              <h2>4. Shipping, Stamped Mail &amp; Customs Disclaimers</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Untracked Stamped Mail Risk Allocation:</strong> Stamped envelope postage is an economical option for photocards and stickers. Stamped mail has no tracking number or postal insurance. <strong>The customer acknowledges and assumes all transit risk of delay, damage, or postal loss.</strong> We cannot refund or reship lost untracked stamped packages.
                </p>
              </div>
              <p>
                <strong>All Sales Final:</strong> All merchandise sales are final. Damage or incorrect item claims on <strong>tracked</strong> shipments must be reported within <strong>7 days of delivery</strong> with an unedited, continuous unboxing video.
              </p>
              <p>
                <strong>International Customs:</strong> International recipients are the importer of record responsible for paying all national import tariffs, VAT, and customs inspection fees.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="legal-doc-section">
              <h2>5. DMCA Copyright Notice &amp; Takedown Protocol</h2>
              <p>
                Star.Ryhwa respects intellectual property rights and adheres strictly to the Digital Millennium Copyright Act (17 U.S.C. § 512). If you are a copyright owner or authorized representative and believe content or items on our site infringe your copyright, please submit a written notification containing the following six statutory elements:
              </p>

              <div className="legal-table-wrapper">
                <table className="legal-table">
                  <thead>
                    <tr>
                      <th>Requirement (17 U.S.C. § 512(c)(3))</th>
                      <th>Details Required in Your Notice</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>1. Authorized Signature</strong></td>
                      <td>A physical or electronic signature of a person authorized to act on behalf of the copyright owner.</td>
                    </tr>
                    <tr>
                      <td><strong>2. Work Identification</strong></td>
                      <td>Identification of the copyrighted work claimed to have been infringed (or representative list).</td>
                    </tr>
                    <tr>
                      <td><strong>3. Infringing Location</strong></td>
                      <td>Identification of the material claimed to be infringing with specific URLs or product titles on Star.Ryhwa.</td>
                    </tr>
                    <tr>
                      <td><strong>4. Contact Information</strong></td>
                      <td>Your full legal name, physical mailing address, telephone number, and active email address.</td>
                    </tr>
                    <tr>
                      <td><strong>5. Good Faith Statement</strong></td>
                      <td>A statement that you have a good-faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law.</td>
                    </tr>
                    <tr>
                      <td><strong>6. Accuracy Statement</strong></td>
                      <td>A statement made under penalty of perjury that the information in the notification is accurate and that you are authorized to act on behalf of the owner.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="legal-footnote">
                <em>Note: Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is infringing may be subject to civil liability for damages.</em>
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="legal-doc-section">
              <h2>6. Designated Copyright Agent &amp; Contact</h2>
              <p>
                Please send all completed statutory DMCA notices or intellectual property inquiries to our designated agent:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Designated Agent:</strong> Copyright Manager, Star.Ryhwa Studio</li>
                <li><strong>Email:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
                <li><strong>Location:</strong> State of Hawaii, United States</li>
              </ul>
            </section>
          </article>

          {/* Sticky Table of Contents Sidebar */}
          <aside className="legal-sidebar">
            <div className="legal-toc-container">
              <div className="legal-toc-title">On This Page</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1" className="legal-toc-link">1. Non-Affiliation Statement</a></li>
                <li><a href="#section-2" className="legal-toc-link">2. First Sale Doctrine</a></li>
                <li><a href="#section-3" className="legal-toc-link">3. Decorative &amp; Safety Notice</a></li>
                <li><a href="#section-4" className="legal-toc-link">4. Stamped Mail &amp; Customs</a></li>
                <li><a href="#section-5" className="legal-toc-link">5. DMCA Takedown Procedure</a></li>
                <li><a href="#section-6" className="legal-toc-link">6. Copyright Agent &amp; Contact</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Disclaimers;
